import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Paperclip, X, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import {
  ACCEPTED_FORMATS_MESSAGE,
  ACCEPT_ATTR,
  MAX_BYTES,
  MAX_FILES,
  BUCKET,
  formatBytes,
  isAcceptedFile,
} from "@/lib/attachments";

export type SaisineAttachmentsHandle = {
  addFiles: (files: FileList | File[]) => void;
};

export type AttachmentManifest = {
  storage_path: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
};

type PendingItem = {
  id: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
  status: "uploading" | "done" | "error";
  manifest?: AttachmentManifest;
  error?: string;
};

function extOf(name: string): string {
  const m = name.toLowerCase().match(/\.[a-z0-9]+$/);
  return m ? m[0].slice(1) : "bin";
}

type Props = {
  userId: string | null;
  items: PendingItem[];
  onChange: (next: PendingItem[]) => void;
};

export const SaisineAttachments = forwardRef<SaisineAttachmentsHandle, Props>(
  function SaisineAttachments({ userId, items, onChange }, ref) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const commitItems = useCallback(
    (updater: (current: PendingItem[]) => PendingItem[]) => {
      const next = updater(itemsRef.current);
      itemsRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  const upload = useCallback(
    async (file: File, id: string) => {
      if (!userId) return;
      const ext = extOf(file.name);
      const path = `user-${userId}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type || "application/octet-stream",
        });
      if (upErr) throw upErr;
      const manifest: AttachmentManifest = {
        storage_path: path,
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        size_bytes: file.size,
      };
      return manifest;
    },
    [userId],
  );

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setError(null);
      if (!userId) {
        setError("Connexion requise pour joindre un fichier.");
        return;
      }
      const currentItems = itemsRef.current;
      const remaining = MAX_FILES - currentItems.length;
      const arr = Array.from(files);
      if (remaining <= 0) {
        setError(`Cette séance porte déjà ${currentItems.length} pièces — c'est le maximum.`);
        return;
      }
      if (arr.length > remaining) {
        setError(`Cette séance porte déjà ${currentItems.length} pièces — c'est le maximum.`);
      }
      const accepted = arr.slice(0, remaining).filter((f) => {
        if (!isAcceptedFile(f)) {
          setError(`${f.name} : format refusé. ${ACCEPTED_FORMATS_MESSAGE}`);
          return false;
        }
        if (f.size > MAX_BYTES) {
          setError(`Fichier trop lourd (${formatBytes(f.size)}, max 15 Mo) : ${f.name}`);
          return false;
        }
        return true;
      });
      if (accepted.length === 0) return;

      const newPendings: PendingItem[] = accepted.map((f) => ({
        id: crypto.randomUUID(),
        filename: f.name,
        size_bytes: f.size,
        mime_type: f.type || "application/octet-stream",
        status: "uploading" as const,
      }));
      commitItems((current) => [...current, ...newPendings]);

      await Promise.all(
        accepted.map(async (file, idx) => {
          const pending = newPendings[idx];
          try {
            const manifest = await upload(file, pending.id);
            commitItems((current) => current.map((p) =>
              p.id === pending.id ? { ...p, status: "done" as const, manifest } : p,
            ));
          } catch (e) {
            const msg = e instanceof Error ? e.message : "Échec de l'upload.";
            commitItems((current) => current.map((p) =>
              p.id === pending.id ? { ...p, status: "error" as const, error: msg } : p,
            ));
            setError(`Échec de l'upload : ${pending.filename}`);
          }
        }),
      );
    },
    [userId, commitItems, upload],
  );

  const removeAt = (id: string) => {
    commitItems((current) => current.filter((p) => p.id !== id));
  };

  useImperativeHandle(ref, () => ({ addFiles: (files) => void handleFiles(files) }), [handleFiles]);


  return (
    <div className="saisine-attachments">
      <style>{`
        .saisine-attachments { display: flex; flex-direction: column; gap: 8px; }
        .saisine-attach-btn {
          display: inline-flex; align-items: center; gap: 6px;
          background: transparent; border: none; padding: 4px 0;
          color: #F5EFE3;
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
          cursor: pointer; transition: color 0.2s;
          align-self: flex-start;
        }
        .saisine-attach-btn:hover:not(:disabled) { color: #FF1744; }
        .saisine-attach-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .saisine-attach-chips {
          display: flex; flex-wrap: wrap; gap: 6px;
        }
        .saisine-attach-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 8px; border: 1px solid rgba(212, 165, 116, 0.3);
          background: rgba(212, 165, 116, 0.04);
          color: var(--qv3-paper, #f3efe6);
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-size: 11px; max-width: 240px;
        }
        .saisine-attach-chip.is-error { border-color: rgba(200, 75, 49, 0.5); }
        .saisine-attach-chip-name {
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          max-width: 160px;
        }
        .saisine-attach-chip-size { opacity: 0.55; font-size: 10px; }
        .saisine-attach-chip-x {
          background: none; border: none; color: inherit; cursor: pointer;
          padding: 0; display: inline-flex; opacity: 0.6;
        }
        .saisine-attach-chip-x:hover { opacity: 1; }
        .saisine-attach-spin { animation: saisine-spin 1s linear infinite; }
        @keyframes saisine-spin { to { transform: rotate(360deg); } }
        .saisine-attach-err {
          color: var(--qv3-led-red, #FF1744);
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-size: 11px;
        }
      `}</style>

      <button
        type="button"
        className="saisine-attach-btn"
        onClick={() => inputRef.current?.click()}
        disabled={!userId || items.length >= MAX_FILES}
        aria-label={items.length >= MAX_FILES ? `Maximum de ${MAX_FILES} pièces atteint` : "Joindre un document"}
        title={items.length >= MAX_FILES ? `Cette séance porte déjà ${items.length} pièces — c'est le maximum.` : ACCEPTED_FORMATS_MESSAGE}
      >
        <Paperclip size={12} strokeWidth={1.5} />
        Joindre un document ({items.length}/{MAX_FILES})
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files) void handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {items.length > 0 && (
        <div className="saisine-attach-chips" aria-label="Documents joints">
          {items.map((p) => (
            <span
              key={p.id}
              className={`saisine-attach-chip${p.status === "error" ? " is-error" : ""}`}
              title={p.filename}
            >
              {p.status === "uploading" ? (
                <Loader2 size={11} className="saisine-attach-spin" />
              ) : null}
              <span className="saisine-attach-chip-name">{p.filename}</span>
              <span className="saisine-attach-chip-size">{formatBytes(p.size_bytes)}</span>
              <button
                type="button"
                className="saisine-attach-chip-x"
                onClick={() => removeAt(p.id)}
                aria-label={`Retirer ${p.filename}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <p className="saisine-attach-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

export type { PendingItem };
